import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import * as Api from '@vi-iot/client';

import { InstallationFeaturesActions, RemoteDiagnosticsStore } from '@store';

import { FeatureNames } from '@vi-iot/client';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';

import { InstallationDetailsStore } from '../installation-details';
import { RemoteDiagnosticsActions } from '../remote-diagnostics/remote-diagnostics.actions';

import { GatewayActions } from './gateway.actions';

@Injectable()
export class GatewayEffects {
  fetchGatewayFeature$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayActions.FetchFeature),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.getSelectedGateway$.pipe(
            filter((selectedGateway) => !!selectedGateway),
            take(1)
          ),
        ])
      ),
      switchMap(([_action, installationId, selectedGateway]) =>
        this.api
          .getGatewayFeature(installationId, selectedGateway?.serial, _action.featureName)
          .pipe(
            map((payload) => {
              if (_action.featureName === FeatureNames.gatewayRemoteDiagnostics) {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_GATEWAY_REMOTE_DIAGNOSTICS_FEATURE_SUCCESS,
                  LogLevelEnum.TRACE,
                  { feature: payload.data }
                );
              }

              return GatewayActions.FetchFeatureSuccess({
                features: [payload.data],
              });
            }),
            catchError((error) => {
              if (_action.featureName === FeatureNames.gatewayRemoteDiagnostics) {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_GATEWAY_REMOTE_DIAGNOSTICS_FEATURE_FAILED,
                  LogLevelEnum.ERROR,
                  { error }
                );
              }

              return of(GatewayActions.FetchFeatureError());
            })
          )
      )
    );
  });

  listGatewayFeaturesSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.fetchFeaturesForInstallationGateways.success),
      map(() => RemoteDiagnosticsActions.loadActiveSessions.attempt())
    );
  });

  fetchUseCasePopupStatus$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InstallationFeaturesActions.fetchFeaturesForInstallationGateways.success),
        map(() => {
          this.remoteDiagnosticsStore.getConfirmPopupFlag();
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private api: Api.OperationalDataApi,
    private actions$: Actions,
    private installationDetailsStore: InstallationDetailsStore,
    private appInsights: AppInsightsMonitoringService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore
  ) {}
}
